import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AuthService } from '../core/services/auth.service';
import { TokenService } from '../core/services/token.service';
import { UserTableService } from '../core/database/user-table.service';
import { PermissionService } from '../core/database/permission.service';
import { UserService } from '../core/services/user.service';
import { AuthErrors } from '../shared/enums/auth-errors';

/** Where people are told to go when Vision cannot fix the problem for them. */
export const SUPPORT_EMAIL = 'systemsupport@acttraining.org.uk';

/** How this screen should behave for one kind of sign-in failure. */
export interface AuthErrorView {
  /**
   * The headline. Not every failure is a failure to SIGN IN — a token refresh, a lost permission set or a
   * forced sign-out all happen to somebody who was already working, and telling them "we could not sign
   * you in" contradicts the sentence underneath it.
   */
  heading: string;
  /** What the person is told. Never mentions the support address — the template renders that as a link. */
  message: string;
  /**
   * Whether signing in again is capable of fixing this.
   *
   * Together with {@link showSupportContact}, this decides whether the page logs the person out on a
   * timer. For a lost token the answer is yes, and being sent to the log-in screen after a few seconds is
   * helpful. For a deactivated account or a duplicated email address it is no: nothing they do at the
   * log-in screen changes the outcome, and logging them out only clears the message off the screen before
   * they have finished reading it.
   */
  resolvedBySigningInAgain: boolean;
  /**
   * Clear Vision's own token and cached user state as soon as this view is shown. This is separate from
   * {@link resolvedBySigningInAgain}: a duplicate/deactivated account must lose its Vision session without
   * being hurried off the explanation, while a bare visit to this route must not alter a valid session.
   */
  clearVisionSessionOnDisplay: boolean;
  /**
   * Show the "email support" line, as a mailto link.
   *
   * A view that shows it never leaves on a timer, even when signing in again could help (#1557 round 20;
   * WCAG 2.2.1 Timing Adjustable). A failed redirect is exactly that case. It used to tell the person to
   * email support and then sign them out five seconds later, taking the address off the screen with no way
   * to pause, extend or dismiss the countdown, and a screen reader could still be reading the alert when
   * it fired. Nobody can be expected to take down an email address inside a countdown they cannot stop.
   */
  showSupportContact: boolean;
}

/**
 * Every message this screen can show, as data rather than as branches inside ngOnInit.
 *
 * As a switch statement these strings were unreachable from a test — the only way to see one was to
 * set a query parameter and run the component — so nothing verified that a given error code produced
 * the right message, or that the codes were distinct. Keyed by AuthErrors, the compiler also requires
 * a new entry whenever a code is added.
 */
export const AUTH_ERROR_VIEWS: Readonly<Record<AuthErrors, AuthErrorView>> = {
  [AuthErrors.RefreshTokenError]: {
    heading: 'Your Vision session has ended',
    message:
      'There was an error while attempting to grab a refresh token. You will need to log in again to rectify the issue.',
    resolvedBySigningInAgain: true,
    clearVisionSessionOnDisplay: false,
    showSupportContact: false,
  },
  [AuthErrors.LostUserDetails]: {
    heading: 'Your Vision session has ended',
    message: 'Your details have been lost. You will need to log in again to rectify the issue.',
    resolvedBySigningInAgain: true,
    clearVisionSessionOnDisplay: false,
    showSupportContact: false,
  },
  [AuthErrors.PermissionsMissing]: {
    heading: 'Your Vision session has ended',
    message: 'Your permissions have been lost. You will need to log in again to rectify the issue.',
    resolvedBySigningInAgain: true,
    clearVisionSessionOnDisplay: false,
    showSupportContact: false,
  },
  [AuthErrors.ForceLogout]: {
    heading: 'You have been signed out',
    message: 'You have been forcefully logged out. Please log in again to continue.',
    resolvedBySigningInAgain: true,
    clearVisionSessionOnDisplay: false,
    showSupportContact: false,
  },
  [AuthErrors.FailedRedirect]: {
    heading: 'We could not sign you in',
    message: 'There was an error while logging you in. Please log in again. If the issue persists:',
    resolvedBySigningInAgain: true,
    // True since this view stopped counting down (#1557 round 20). The countdown was what cleared this
    // browser's Vision session, five seconds in. Without it nothing would clear the session until the
    // person pressed a button they may never press, which is exactly what the refused-account views,
    // the other views with no countdown, already avoid by clearing on display.
    //
    // It is also what offers the person "Return to sign-in" rather than "Return to Vision": the template
    // picks the way out from this flag, and somebody whose sign-in has just failed must not be offered a
    // link back into the app. A spec test holds this for every error code with no countdown.
    clearVisionSessionOnDisplay: true,
    showSupportContact: true,
  },
  [AuthErrors.UserNotActive]: {
    heading: 'We could not sign you in',
    // Softened deliberately (#1557, Jack's review 26 Aug). This is the CATCH-ALL: every refusal the API
    // does not name by itself lands here — a closed account, someone who has left, a record already
    // linked to a different Microsoft account, a sign-in under an alias, or a configuration fault at our
    // end. "Your account is not active" was only true for the first of those, and asserted something
    // specific and false to everyone else — the same wrong-message defect this branch fixed for the
    // duplicate-address case.
    //
    // "We could not MATCH your sign-in to an account" was the first attempt and was also wrong: for a
    // leaver or a closed account we matched them perfectly well and then refused, so it points them and
    // the support desk at "my account was never set up". It also contradicted itself — no account
    // matched, then "THE account has been closed", with nothing for "the" to refer to. This wording
    // says only what is true of all five: we could not sign them in with this account.
    message:
      'We could not sign you into Vision with this account. That may be because it has been closed, or ' +
      'because something on our side needs putting right:',
    resolvedBySigningInAgain: false,
    clearVisionSessionOnDisplay: true,
    showSupportContact: true,
  },
  [AuthErrors.DuplicateAccounts]: {
    heading: 'We could not sign you in',
    // Says what is wrong, that it is not their fault, and exactly who fixes it. The previous shared
    // message ('your account is not active') was false for these people and sent both them and
    // support looking in the wrong place (#1557).
    //
    // Deliberately does NOT say "your email address" (#1557 review, 27 August). Of the 29 people who
    // reach this screen, 25 share an address and 4 share a Microsoft account — and those 4 have
    // DIFFERENT addresses, so naming the address would be false for them in the same way "your account
    // is not active" was false for all 29. "Belongs to you" is true of both groups, and the remedy is
    // identical either way, so the reader needs no more detail than this.
    message:
      'More than one Vision account belongs to you, so we cannot tell which one is yours. ' +
      'This needs to be corrected by us:',
    resolvedBySigningInAgain: false,
    clearVisionSessionOnDisplay: true,
    showSupportContact: true,
  },
};

/**
 * A code we do not recognise. The person got here from a real failure, so signing them out and letting
 * them start again is the right move.
 */
const UNEXPECTED_ERROR_VIEW: AuthErrorView = {
  heading: 'Something went wrong',
  message: 'An unexpected authentication error occurred. Please log in again.',
  resolvedBySigningInAgain: true,
  clearVisionSessionOnDisplay: false,
  showSupportContact: false,
};

/**
 * No error code at all — a bookmark, a shared link, or a reload that lost the query string.
 *
 * Deliberately does NOT sign the person out. Nothing has actually gone wrong, so taking their session
 * away would be a real consequence for simply opening a URL; they get an explanation and a way back
 * instead. Distinct from {@link UNEXPECTED_ERROR_VIEW}, which answers "a failure we cannot name" —
 * this one answers "no failure was reported".
 */
const NO_ERROR_CODE_VIEW: AuthErrorView = {
  heading: 'Nothing to show here',
  message:
    'This page explains why you could not be signed in, but no reason was passed to it. If you were ' +
    'trying to use Vision, please start again from the sign-in page.',
  resolvedBySigningInAgain: false,
  clearVisionSessionOnDisplay: false,
  showSupportContact: false,
};

@Component({
  selector: 'app-auth-error',
  templateUrl: './auth-error.component.html',
  styleUrls: ['./auth-error.component.scss'],
})
export class AuthErrorComponent implements OnInit, AfterViewInit, OnDestroy {
  heading = '';
  errorMessage = '';
  showSupportContact = false;
  supportEmail = SUPPORT_EMAIL;

  /**
   * Whether this screen will log the person out by itself. False leaves the message on screen until
   * they choose to leave, via whichever of the template's two controls is shown in its place.
   */
  autoLogout = true;
  clearVisionSessionOnDisplay = false;

  count = 5;
  countMessage = this.countdownMessage();
  refreshInterval: ReturnType<typeof setInterval> | undefined;

  /**
   * The browser tab's title from before this page set its own, put back when the page closes
   * (#1557 round 20; WCAG 2.4.2 Page Titled).
   */
  private titleBeforeThisPage: string | undefined;

  /** Focused on load so a screen reader announces the message instead of leaving it unread. */
  @ViewChild('errorAlert') errorAlert: ElementRef<HTMLElement> | undefined;

  constructor(
    public authService: AuthService,
    private readonly tokenService: TokenService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly userTableService: UserTableService,
    private readonly permissionService: PermissionService,
    private readonly userService: UserService,
    private readonly titleService: Title
  ) {}

  ngOnInit(): void {
    // A visit with no error code still has to say something useful. It used to render the heading over an
    // empty message with a countdown that promised a sign-out and never delivered one, and no button —
    // a dead end reachable by bookmarking the page or losing the query string.
    const errorNumber = new URLSearchParams(window.location.search).get('error');
    if (errorNumber == null) {
      this.apply(NO_ERROR_CODE_VIEW);
      return;
    }
    this.showError(parseInt(errorNumber));
  }

  /**
   * Decides what this screen says and how it behaves for one error code.
   *
   * Split out from ngOnInit so it can be exercised directly. The code arrives as a query parameter,
   * and the only way to drive that from a test is to rewrite the browser's address bar — which Karma
   * reports as a full page reload and which costs the whole run. Reading the parameter stays in
   * ngOnInit, unchanged; everything that decides what a person sees lives here.
   */
  showError(code: AuthErrors): void {
    this.apply(AUTH_ERROR_VIEWS[code] ?? UNEXPECTED_ERROR_VIEW);
  }

  private apply(view: AuthErrorView): void {
    // Clear first: this method is public and can be called more than once, and assigning over a running
    // interval would leave the previous one ticking with nothing able to stop it.
    clearInterval(this.refreshInterval);
    this.refreshInterval = undefined;

    this.heading = view.heading;
    this.errorMessage = view.message;

    // WCAG 2.4.2 Page Titled. This page replaces the whole view after a failed sign-in, and a tab that
    // still says "Vision" tells a screen-reader user who arrives here by navigation nothing about where they
    // are. Named after the heading, in the "<page> - Vision" form the About page uses. The earlier title is
    // captured only once, so showing a second error does not replace it with the first error's title.
    this.titleBeforeThisPage ??= this.titleService.getTitle();
    this.titleService.setTitle(`${view.heading} - Vision`);

    this.showSupportContact = view.showSupportContact;
    // Never on a timer while the support address is on screen: see AuthErrorView.showSupportContact.
    this.autoLogout = view.resolvedBySigningInAgain && !view.showSupportContact;
    this.clearVisionSessionOnDisplay = view.clearVisionSessionOnDisplay;
    this.count = 5;
    this.countMessage = this.countdownMessage();

    if (this.autoLogout) {
      this.refreshInterval = setInterval(() => {
        this.countDown();
      }, 1000);
      return;
    }

    if (this.clearVisionSessionOnDisplay) {
      // Duplicate/deactivated accounts must lose Vision's own token and cached authorisation immediately,
      // while the Microsoft session remains until the person chooses the button below. A no-code visit takes
      // neither path: nothing has failed, so it must preserve the existing Vision session too.
      this.clearStoredSession();
    }
  }

  ngAfterViewInit(): void {
    this.errorAlert?.nativeElement.focus();
  }

  ngOnDestroy(): void {
    clearInterval(this.refreshInterval);
    // Put the tab title back. No other page sets one, so leaving by "Return to Vision" would otherwise leave
    // the tab named after this page ("Nothing to show here - Vision") for the rest of the session.
    if (this.titleBeforeThisPage !== undefined) {
      this.titleService.setTitle(this.titleBeforeThisPage);
    }
  }

  countDown(): void {
    this.count--;
    this.countMessage = this.countdownMessage();
    if (this.count === 0) {
      this.logOut();
    }
    this.changeDetectorRef.detectChanges();
  }

  /**
   * The countdown sentence, built in one place (#1557 round 20). It used to be written out twice with two
   * wordings, "taken to the log out screen" before the first tick and "logged out" after it, so the sentence
   * changed under the reader one second in, and on the last tick it said "1 seconds". "Signed out" matches
   * the sentence the alert announces to screen readers, so both are told the same thing.
   */
  private countdownMessage(): string {
    return `You will be signed out in ${this.count} ${this.count === 1 ? 'second' : 'seconds'}.`;
  }

  /** Shared by the countdown reaching zero and the button shown when there is no countdown. */
  logOut(): void {
    clearInterval(this.refreshInterval);
    // ORDER IS LOAD-BEARING. authService.logout() opens with the sign-out beacon, which sends the CACHED
    // access token so the server can tell who signed out (#1651 Phase C). clearStoredSession() removes
    // that token, so running it first left the beacon tokenless and every sign-out from this page
    // unattributed — the service takes care to get this order right internally, and this method undid it.
    // logoutRedirect() starts a navigation but does not block, so the clean-up below still runs.
    this.authService.logout();
    this.clearStoredSession();
  }

  /**
   * Drop everything this browser holds about the signed-in user, without navigating anywhere.
   * <p>
   * Idempotent, so calling it on arrival and again from the button is harmless. Deliberately separate
   * from {@link logOut}, which additionally redirects to Microsoft to end the Entra session — that is a
   * navigation, and on this page it would remove the message before it had been read.
   */
  private clearStoredSession(): void {
    // Stop the five-minute user-info poll FIRST. Without this, clearing the session did not stop the
    // timer that refills it: the next tick calls the API, silently refreshes a token and writes the user
    // row back, so a page whose whole job is to end the session quietly un-ended it.
    this.userService.stopUserInfoSync();
    this.tokenService.removeAccessToken();
    this.userTableService.clear();
    this.permissionService.clear();
    this.authService.setUiAuthenticated(false);
  }
}
